import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import ProductDetailsFields from './ProductDetailsFields';
import ProductImagesTab from './ProductImagesTab';
import ProductFeaturesTab from './ProductFeaturesTab';
import ProductSpecificationsTab from './ProductSpecificationsTab';
import ProductPromoBannerTab from './ProductPromoBannerTab';
import ProductDetailTabsTab from './ProductDetailTabsTab';
import { mapProductFromApiAsync } from './utils/mapProduct';

function EditProduct({
  ProductData,
  sendDataToParent,
  onFeatureOrderChange,
  featureOrderSaveToken = 0,
  onSpecificationOrderChange,
  specOrderSaveToken = 0,
  onDetailTabsChange,
  detailTabsSaveToken = 0,
  tabTypes = [],
}) {
  const [formData, setFormData] = useState(ProductData || {});
  const [slugManual, setSlugManual] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  const productId = formData?.ProductId;

  const refreshProduct = async () => {
    if (!productId) return;
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product/getById`, {
        params: { ProductId: productId },
      });
      if (response.data?.data) {
        const mapped = await mapProductFromApiAsync(response.data.data);
        setFormData(mapped);
        if (sendDataToParent) sendDataToParent(mapped);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setFormData(ProductData || {});
  }, [ProductData]);

  const handleData = (data) => {
    setFormData(data);
    if (sendDataToParent) sendDataToParent(data);
  };

  const handleDetailTabsChange = useCallback((change) => {
    if (change?.tabs) {
      setFormData((prev) => {
        const next = { ...prev, DetailTabs: change.tabs };
        if (sendDataToParent) sendDataToParent(next);
        return next;
      });
    }
    if (onDetailTabsChange) onDetailTabsChange(change);
  }, [onDetailTabsChange, sendDataToParent]);

  const childTabsEnabled = !!productId;

  return (
    <div className="card-body px-0">
      <nav className="nav nav-style-6 nav-pills mb-3 nav-justified d-sm-flex d-block" role="tablist">
        <button type="button" className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
          Product Details
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'images' ? 'active' : ''}`}
          onClick={() => childTabsEnabled && setActiveTab('images')}
          disabled={!childTabsEnabled}
          title={!childTabsEnabled ? 'Save product details first' : ''}
        >
          Upload Images
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'features' ? 'active' : ''}`}
          onClick={() => childTabsEnabled && setActiveTab('features')}
          disabled={!childTabsEnabled}
        >
          Product Features
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'specs' ? 'active' : ''}`}
          onClick={() => childTabsEnabled && setActiveTab('specs')}
          disabled={!childTabsEnabled}
        >
          Product Specifications
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'promo' ? 'active' : ''}`}
          onClick={() => childTabsEnabled && setActiveTab('promo')}
          disabled={!childTabsEnabled}
          title={!childTabsEnabled ? 'Save product details first' : ''}
        >
          Promo Banner
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'detail-tabs' ? 'active' : ''}`}
          onClick={() => childTabsEnabled && setActiveTab('detail-tabs')}
          disabled={!childTabsEnabled}
          title={!childTabsEnabled ? 'Save product details first' : ''}
        >
          Detail Page Tabs
        </button>
      </nav>

      <div className="tab-content">
        {activeTab === 'details' && (
          <ProductDetailsFields
            formData={formData}
            sendDataToParent={handleData}
            slugManual={slugManual}
            onSlugManualChange={setSlugManual}
          />
        )}
        {activeTab === 'images' && (
          <ProductImagesTab
            productId={productId}
            images={formData.Images}
            isPromoBanner={!!formData.IsPromoBanner}
            onRefresh={refreshProduct}
          />
        )}
        {activeTab === 'features' && (
          <ProductFeaturesTab
            productId={productId}
            features={formData.Features}
            onRefresh={refreshProduct}
            onOrderChange={onFeatureOrderChange}
            featureOrderSaveToken={featureOrderSaveToken}
          />
        )}
        {activeTab === 'specs' && (
          <ProductSpecificationsTab
            productId={productId}
            specifications={formData.Specifications}
            onRefresh={refreshProduct}
            onOrderChange={onSpecificationOrderChange}
            specOrderSaveToken={specOrderSaveToken}
          />
        )}
        {activeTab === 'promo' && (
          <ProductPromoBannerTab
            formData={formData}
            sendDataToParent={handleData}
          />
        )}
        {activeTab === 'detail-tabs' && (
          <ProductDetailTabsTab
            detailTabs={formData.DetailTabs}
            tabTypes={tabTypes}
            onTabsChange={handleDetailTabsChange}
            detailTabsSaveToken={detailTabsSaveToken}
          />
        )}
      </div>
    </div>
  );
}

export default EditProduct;
